import { apiService } from './api';

/**
 * Project Contacts Service - Handles all project contact-related API operations
 * Uses general /contacts endpoints with projectId in request body/params
 */
export const projectContactsService = {
  /**
   * Get all contacts for a specific project
   * @param {string} projectId - Project ID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.search - Search term for name or phone number
   * @param {string} params.status - Filter by status
   * @param {string} params.sortBy - Sort field
   * @param {string} params.sortOrder - Sort order (asc/desc)
   * @returns {Promise<Object>} Contacts data with pagination
   */
  getProjectContacts: async (projectId, params = {}) => {
    // Filter out empty values
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
    );
    const queryParams = new URLSearchParams({ projectId, ...cleanParams });
    const url = `/contacts?${queryParams.toString()}`;
    return await apiService.get(url);
  },

  /**
   * Get a single contact by ID within a project
   * @param {string} projectId - Project ID
   * @param {string} contactId - Contact ID
   * @returns {Promise<Object>} Contact data
   */
  getProjectContact: async (projectId, contactId) => {
    const queryParams = new URLSearchParams({ projectId });
    const url = `/contacts/${contactId}?${queryParams.toString()}`;
    return await apiService.get(url);
  },

  /**
   * Create a new contact for a specific project
   * @param {string} projectId - Project ID
   * @param {Object} contactData - Contact data
   * @param {string} contactData.name - Contact name
   * @param {string} contactData.phoneNumber - Phone number
   * @param {string} contactData.email - Email address (optional)
   * @param {string} contactData.company - Company name (optional)
   * @param {string} contactData.status - Contact status (optional)
   * @returns {Promise<Object>} Created contact data
   */
  createProjectContact: async (projectId, contactData) => {
    return await apiService.post('/contacts', {
      projectId,
      ...contactData,
    });
  },

  /**
   * Update an existing contact within a project
   * @param {string} projectId - Project ID
   * @param {string} contactId - Contact ID
   * @param {Object} contactData - Updated contact data
   * @returns {Promise<Object>} Updated contact data
   */
  updateProjectContact: async (projectId, contactId, contactData) => {
    return await apiService.put(`/contacts/${contactId}`, {
      projectId,
      ...contactData,
    });
  },

  /**
   * Delete a contact from a project
   * @param {string} projectId - Project ID
   * @param {string} contactId - Contact ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteProjectContact: async (projectId, contactId) => {
    const queryParams = new URLSearchParams({ projectId });
    const url = `/contacts/${contactId}?${queryParams.toString()}`;
    return await apiService.delete(url);
  },

  /**
   * Bulk import contacts for a specific project
   * @param {string} projectId - Project ID
   * @param {Array} contacts - Array of contact objects
   * @param {Object} options - Import options
   * @param {string} options.actionOnDuplicate - Action to take on duplicates ('skip' or 'replace')
   * @returns {Promise<Object>} Import results
   */
  importProjectContacts: async (projectId, contacts, options = {}) => {
    const payload = {
      projectId,
      actionOnDuplicate: options.actionOnDuplicate || 'skip',
      contacts: contacts.map(contact => ({
        name: contact.name,
        phoneNumber: contact.phoneNumber,
        dateOfBirth: contact.dateOfBirth || null,
        tags: contact.tags || [],
        notes: contact.notes || null,
      })),
    };

    return await apiService.post('/contacts/bulk', payload);
  },

  /**
   * Export contacts for a specific project to Excel/CSV
   * @param {string} projectId - Project ID
   * @param {Object} params - Export parameters
   * @param {string} params.format - Export format (xlsx, csv)
   * @param {Array} params.fields - Fields to export
   * @param {Object} params.filters - Filter criteria
   * @returns {Promise<Blob>} File blob
   */
  exportProjectContacts: async (projectId, params = {}) => {
    const queryParams = new URLSearchParams();

    // Add projectId as query parameter
    queryParams.append('projectId', projectId);

    if (params.format) queryParams.append('format', params.format);
    if (params.fields) queryParams.append('fields', params.fields.join(','));
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (key === 'projectId') return; // Already added above
        if (value) queryParams.append(key, value);
      });
    }

    const url = `/contacts/export${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}${url}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getCookie('accessToken')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return await response.blob();
  },

  /**
   * Bulk update contacts for a specific project
   * @param {string} projectId - Project ID
   * @param {Array} contactIds - Array of contact IDs
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Update results
   */
  bulkUpdateProjectContacts: async (projectId, contactIds, updateData) => {
    return await apiService.patch('/contacts/bulk-update', {
      projectId,
      contactIds,
      updateData,
    });
  },

  /**
   * Bulk delete contacts for a specific project
   * @param {string} projectId - Project ID
   * @param {Array} contactIds - Array of contact IDs
   * @returns {Promise<Object>} Deletion results
   */
  bulkDeleteProjectContacts: async (projectId, contactIds) => {
    return await apiService.delete('/contacts/bulk-delete', {
      data: {
        projectId,
        contactIds,
      },
    });
  },

  /**
   * Download sample contact import template
   * @returns {Promise<Blob>} File blob
   */
  getContactImportTemplate: async () => {
    return await apiService.get('/contacts/template', {
      responseType: 'blob',
    });
  },
};

// Helper function to get cookie value
const getCookie = name => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export default projectContactsService;
